import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { fetchStoreOptions, fetchStoreVcOptions, fetchStoreWalmartDsvOptions, type StoreOptionItem, type StoreVcOptionItem } from "@app/api/store";

export interface StoreSelectOption {
    label: string;
    value: string;
}

export interface VcCodeSelectOption {
    label: string;
    value: string;
}

/** 与后端 StorePlatformEnum 一致 */
const STORE_PLATFORM_AMAZON = 1;
const STORE_PLATFORM_WALMART = 2;

/** VC 类型统一大写比较（PO / DF / DSV）；空值与 "--" 等占位值视为未知，不参与过滤 */
function normalizeVcType(value?: string | null): string {
    const text = String(value ?? "")
        .trim()
        .toUpperCase();
    return /^[A-Z]+$/.test(text) ? text : "";
}

/** 管理端店铺下拉（会话内缓存，登录后或列表刷新时拉取） */
export const useStoreOptionsStore = defineStore("storeOptions", () => {
    const items = ref<StoreOptionItem[]>([]);
    const vcItems = ref<StoreVcOptionItem[]>([]);
    const walmartItems = ref<StoreVcOptionItem[]>([]);
    const loaded = ref(false);
    const vcLoaded = ref(false);
    const walmartLoaded = ref(false);
    const loading = ref(false);
    const vcLoading = ref(false);
    const walmartLoading = ref(false);

    const selectOptions = computed<StoreSelectOption[]>(() =>
        items.value.map(item => ({
            label: item.storeName,
            value: item.id,
        })),
    );

    /** 全部店铺去重后的 VC Code（列表多选筛选） */
    const vcCodeSelectOptions = computed<VcCodeSelectOption[]>(() => {
        const map = new Map<string, string>();

        function addCode(code?: string | null, name?: string | null) {
            const value = String(code ?? "").trim();
            if (!value || map.has(value)) return;
            map.set(value, name?.trim() ? `${value}（${name}）` : value);
        }

        vcItems.value.forEach(store => {
            addCode(store.vcCode);
            (store.vcCodes || []).forEach(item => addCode(item.code, item.name));
        });
        walmartItems.value.forEach(store => {
            addCode(store.vcCode);
            (store.vcCodes || []).forEach(item => addCode(item.code));
        });
        items.value.forEach(store => addCode(store.vcCode));

        return Array.from(map.entries())
            .map(([value, label]) => ({ label, value }))
            .sort((a, b) => a.value.localeCompare(b.value));
    });

    async function fetchOptions(force = false) {
        if (loading.value) return;
        if (loaded.value && !force) return;

        loading.value = true;
        try {
            const data = await fetchStoreOptions();
            items.value = data.items ?? [];
            loaded.value = true;
        } catch (e) {
            console.error("[storeOptions]", e);
            if (force) items.value = [];
        } finally {
            loading.value = false;
        }
    }

    async function fetchVcOptions(force = false) {
        if (vcLoading.value) return;
        if (vcLoaded.value && !force) return;

        vcLoading.value = true;
        try {
            const data = await fetchStoreVcOptions();
            vcItems.value = data.items ?? [];
            vcLoaded.value = true;
        } catch (e) {
            console.error("[storeOptions.vcOptions]", e);
            if (force) vcItems.value = [];
        } finally {
            vcLoading.value = false;
        }
    }

    function fetchIfNeeded() {
        return fetchOptions(false);
    }

    function fetchVcIfNeeded() {
        return fetchVcOptions(false);
    }

    async function fetchWalmartDsvOptions(force = false) {
        if (walmartLoading.value) return;
        if (walmartLoaded.value && !force) return;
        walmartLoading.value = true;
        try {
            const data = await fetchStoreWalmartDsvOptions();
            walmartItems.value = data.items ?? [];
            walmartLoaded.value = true;
        } catch (e) {
            console.error("[storeOptions.walmartDsv]", e);
            if (force) walmartItems.value = [];
        } finally {
            walmartLoading.value = false;
        }
    }

    function fetchWalmartDsvIfNeeded() {
        return fetchWalmartDsvOptions(false);
    }

    function refresh() {
        return Promise.all([fetchOptions(true), fetchVcOptions(true), fetchWalmartDsvOptions(true)]);
    }

    function reset() {
        items.value = [];
        vcItems.value = [];
        walmartItems.value = [];
        loaded.value = false;
        vcLoaded.value = false;
        walmartLoaded.value = false;
        loading.value = false;
        vcLoading.value = false;
        walmartLoading.value = false;
    }

    function findById(id: string) {
        return items.value.find(item => item.id === id);
    }

    function findVcStoreById(id: string) {
        return vcItems.value.find(item => item.id === id);
    }

    function findWalmartStoreById(id: string) {
        return walmartItems.value.find(item => item.id === id);
    }

    function mapStoreCodes(store: StoreVcOptionItem | undefined, vcType?: string | null, withName = true): VcCodeSelectOption[] {
        if (!store) return [];
        const expectType = normalizeVcType(vcType);
        return (store.vcCodes || [])
            .filter(item => {
                if (!expectType) return true;
                const type = normalizeVcType(item.type);
                return !type || type === expectType;
            })
            .map(item => {
                const code = (item.code || "").trim();
                if (!code) return null;
                return {
                    label: withName && item.name?.trim() ? `${code}（${item.name}）` : code,
                    value: code,
                };
            })
            .filter((item): item is VcCodeSelectOption => Boolean(item));
    }

    /**
     * 某店铺下的 VC Code 单选选项。
     * 传 vcType 时只保留同类型的 code（PO 报价不给选 DF 的 code，避免选错）；
     * 接口未返回 type 的条目不过滤，避免数据缺失时选不到。
     */
    function getVcCodeOptionsByStoreId(storeId?: string | null, vcType?: string | null): VcCodeSelectOption[] {
        if (!storeId || storeId === "0") return [];
        return mapStoreCodes(findVcStoreById(storeId), vcType);
    }

    /** 按平台过滤启用店铺（核价弹窗：Amazon 行不展示 Walmart 店） */
    function getStoreSelectOptionsByPlatform(platform?: number | null): StoreSelectOption[] {
        const list = platform ? items.value.filter(item => item.platform === platform) : items.value;
        return list.map(item => ({
            label: item.storeName,
            value: item.id,
        }));
    }

    /** 按平台取去重后的 VC Code（列表筛选：未选平台返回空） */
    function getVcCodeSelectOptionsByPlatform(platform?: number | null): VcCodeSelectOption[] {
        let stores: StoreVcOptionItem[] = [];
        if (platform === STORE_PLATFORM_WALMART) {
            stores = walmartItems.value;
        } else if (platform === STORE_PLATFORM_AMAZON) {
            stores = vcItems.value;
        } else {
            return [];
        }
        const withName = platform !== STORE_PLATFORM_WALMART;
        const map = new Map<string, string>();
        function addCode(code?: string | null, name?: string | null) {
            const value = String(code ?? "").trim();
            if (!value || map.has(value)) return;
            map.set(value, withName && name?.trim() ? `${value}（${name}）` : value);
        }
        stores.forEach(store => {
            addCode(store.vcCode);
            (store.vcCodes || []).forEach(item => addCode(item.code, withName ? item.name : undefined));
        });
        return Array.from(map.entries())
            .map(([value, label]) => ({ label, value }))
            .sort((a, b) => a.value.localeCompare(b.value));
    }

    /** 按平台取店铺下的 code：Amazon 走 vcOptions，Walmart 走 walmartDsv */
    function getCodeOptionsByStore(storeId?: string | null, platform?: number | null, vcType?: string | null): VcCodeSelectOption[] {
        if (!storeId || storeId === "0") return [];
        if (platform === STORE_PLATFORM_WALMART) {
            return mapStoreCodes(findWalmartStoreById(storeId), vcType, false);
        }
        return getVcCodeOptionsByStoreId(storeId, vcType);
    }

    return {
        items,
        vcItems,
        walmartItems,
        loaded,
        vcLoaded,
        walmartLoaded,
        loading,
        vcLoading,
        walmartLoading,
        selectOptions,
        vcCodeSelectOptions,
        fetchIfNeeded,
        fetchVcIfNeeded,
        fetchWalmartDsvIfNeeded,
        refresh,
        reset,
        findById,
        findVcStoreById,
        getVcCodeOptionsByStoreId,
        getStoreSelectOptionsByPlatform,
        getVcCodeSelectOptionsByPlatform,
        getCodeOptionsByStore,
    };
});
